import { useReduxState } from 'hooks';
import { syntaxHighlightLanguages } from 'lib/lang';
import { Dropdown, DropdownButton } from 'components/common/dropdown';
import { Checkbox, Form } from 'components/common/form';
import { PreEditor } from 'components/editor/inputs';

import type { BlockPre } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

const EditorBlockPre = ({
  blockId,
  data,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockPre> & StateProps) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const { messages, theme } = useReduxState();

  const handleChange = (newText: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            text: newText
          }
        }
      }
    });
  };

  const handleLanguageChange = (ev) => {
    const language = ev.currentTarget.getAttribute('data-value');
    updateData({ ...data, language });
  };

  const handleShowLineNumbersChange = (value: boolean) => {
    updateData({ ...data, showLineNumbers: value });
  };

  return (
    <div className="code">
      <div className="controls">
        <div className="syntax-highlighting">
          <Dropdown
            align="left"
            category="lighter"
            disabled={isPreview}
            isMenu={true}
            label={messages.editorBlockPreSyntaxHighlighting}
            showArrow={true}
            styleContentButtons={false}
            title={
              data.language
                ? syntaxHighlightLanguages[data.language]
                : messages.editorBlockPrePlainText
            }
            tooltip={messages.editorBlockPreSyntaxHighlighting}
          >
            {['plaintext', ...Object.keys(syntaxHighlightLanguages)].map(
              (syntaxLanguage) => (
                <DropdownButton
                  data-value={
                    syntaxLanguage === 'plaintext' ? null : syntaxLanguage
                  }
                  key={syntaxLanguage}
                  isSelected={syntaxLanguage === (data.language || 'plaintext')}
                  onClick={handleLanguageChange}
                  role="menuitemradio"
                  text={
                    syntaxLanguage === 'plaintext'
                      ? messages.editorBlockPrePlainText
                      : syntaxHighlightLanguages[syntaxLanguage]
                  }
                />
              )
            )}
          </Dropdown>
        </div>

        <div className="line-numbers">
          <Form>
            <Checkbox
              disabled={isPreview || Boolean(editorLanguageActive)}
              field="showLineNumbers"
              id={`editor-block-pre-line-numbers-${blockId}`}
              initialValue={Boolean(data.showLineNumbers)}
              label={messages.editorBlockPreShowLineNumbers}
              onValueChange={handleShowLineNumbersChange}
            />
          </Form>
        </div>
      </div>

      <PreEditor
        isPreview={isPreview}
        language={data.language || 'plaintext'}
        onChange={handleChange}
        placeholder={messages.commonAddText}
        showLineNumbers={data.showLineNumbers}
        text={data.locales[languageVersion].translatable.text || ''}
        translationReferenceLanguage={
          editorLanguageActive ? editorLanguageReference : null
        }
        translationReferenceText={
          editorLanguageActive
            ? data.locales[editorLanguageReference]?.translatable?.text || ''
            : null
        }
      />

      <style jsx>{`
        .controls {
          padding: 0 0 15px;
        }
        .syntax-highlighting {
          display: inline-block;
          margin-right: 10px;
        }
        .line-numbers {
          background-color: ${theme.lightGrey};
          border-radius: 5px;
          display: inline-block;
          padding: 6px 10px;
          position: relative;
          top: -1px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockPre;
